import { Form, FormField } from '@/Core/UI/Form'
import { Button, Card, Space, theme } from 'antd'
import { GrClose } from 'react-icons/gr'

import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { userManager } from '@/Core/Managers'
import { useCrudResourceContext } from '../CrudResourceContextProvider'
import { getBaseFilterFields, getFilterFieldsOptionKey, resolveFilterFieldSettings, toVisibleFieldKeys } from '../Helpers/tableSettings'

const { useToken } = theme

export const Filter = observer(() => {
    const crudContext = useCrudResourceContext()
    const { token } = useToken()
    const { t } = useTranslation()

    if (!crudContext?.filterFormContext) {
        console.warn('[Filter] filterFormContext is not available')
        return null
    }

    // Базовий доступний набір полів (обмеження розробника через includeFields, інакше всі поля)
    // + персональні налаштування (видимість + порядок), реактивно з userManager
    const baseFilterFields = getBaseFilterFields(crudContext.filterFormContext)
    const resourceKey = crudContext.resource?.key
    const savedFilter = resourceKey ? userManager.getUserOption(getFilterFieldsOptionKey(resourceKey)) : undefined
    const visibleKeys = toVisibleFieldKeys(resolveFilterFieldSettings(baseFilterFields, savedFilter))
    const visibleKeySet = new Set(visibleKeys)

    let fields = []

    crudContext.filterFormContext?.fields?.forEach((field) => {
        if (!visibleKeySet.has(field.name)) return

        let fieldProps = {}

        if (
            crudContext.filterFormContext.props?.fieldProps &&
            typeof crudContext.filterFormContext.props?.fieldProps[field.name] === 'object'
        )
            fieldProps = crudContext.filterFormContext.props?.fieldProps[field.name]
        
        if (field.type === 'number' && field.definition?.isFilterRange === true) {
            fields.push(
                <FormField
                    key={field.name}
                    name={field.name}
                    {...fieldProps}
                    rangeSelectComponent={
                        <FormField
                            key={field.name}
                            name={`before_${field.name}`}
                            type="select"
                            style={{ width: 70 }}
                            options={[
                                { label: '=', value: 0 },
                                { label: '<', value: 'lt' },
                                { label: '<=', value: 'lte' },
                                { label: '>', value: 'gt' },
                                { label: '>=', value: 'gte' },
                            ]}
                            inputProps={{ defaultValue: 0 }}
                            ignoreParentField={true}
                        />
                    }
                    template={observer(({ field, children, formContext }) => {
                        if (field === undefined) return

                        const error = field.formContext.getError(field.path)
                        // TODO: field.isValid - ??
                        return (
                            <div
                                className={`form-field form-field-default form-field-range ${field.error ? 'is-error' : ''}`}
                            >
                                {field.label && (
                                    <label htmlFor={field.id}>
                                        {field.label}
                                        {field.isRequired && <span className="is-required">*</span>}
                                    </label>
                                )}
                                <Space.Compact>
                                    {field.props.rangeSelectComponent}
                                    {children}
                                </Space.Compact>
                                {<small>{error}</small>}
                            </div>
                        )
                    })}
                />
            )
        } else fields.push(<FormField key={field.name} name={field.name} {...fieldProps} />)
    })

    // !!! Move to CrudResource component
    if (Array.isArray(crudContext.filterFormContext.props.addFields))
        crudContext.filterFormContext.props.addFields.map((field) => {
            if (fields.filter((f) => f.key === field.name).length === 0) {
                fields.push(<FormField key={field.name} {...field} />)
            }
        })

    if (visibleKeys.length > 0) {
        const orderMap = visibleKeys.reduce((acc, key, index) => {
            acc[key] = index
            return acc
        }, {})
        fields.sort((a, b) => (orderMap[a.key] ?? Number.MAX_SAFE_INTEGER) - (orderMap[b.key] ?? Number.MAX_SAFE_INTEGER))
    }

    crudContext.filterFormContext.eventManager.emit('onFilterBuild', { crudContext })

    const filterClear = () => {
        crudContext.listContext.setFilter({})
        crudContext.filterFormContext.reset()
        crudContext.listContext.actions.load(true)

        if (typeof crudContext.storages?.singleFilterForms === 'object') {
            Object.keys(crudContext.storages.singleFilterForms).map((key) => {
                const form = crudContext.storages.singleFilterForms[key]
                form.setInitialValues({})
            })
        }

        if (crudContext.isMobileMode) crudContext.setIsOpenMobileFilter(false)
    }

    const submitHandler = () => {
        crudContext.listContext.setFilter({
            ...crudContext.listContext.filter,
            ...crudContext.filterFormContext.values,
        })
        crudContext.listContext.actions.load()
    }

    crudContext.filterFormContext.addEventHandlers('onChange', { submitHandler })

    return (
        <div style={{ width: 300 }} className="filter-col">
            <Card
                className="wl-card"
                title={<span style={{ fontSize: 20 }}>{t('Filters', { ns: 'Core' })}</span>}
                variant="borderless"
                extra={
                    <a
                        onClick={() => {
                            if (crudContext.isMobileMode) crudContext.setIsOpenMobileFilter(false)
                            else crudContext.setIsFilterOpen(false)
                        }}
                    >
                        <GrClose style={{ fontSize: 22, color: token.colorPrimary, marginTop: 10 }} />
                    </a>
                }
                actions={[
                    <Button size="middle" key="crudresource-filter-apply" type="primary" onClick={submitHandler}>
                        {t('Filter', { ns: 'Core' })}
                    </Button>,
                    <Button size="middle" key="crudresource-filter-clear" onClick={filterClear}>
                        {t('Clear', { ns: 'Core' })}
                    </Button>,
                ]}
            >
                <Form formContext={crudContext.filterFormContext} isLanguageSwitcher={false}>
                    {fields}
                </Form>
            </Card>
        </div>
    )
})
